import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../../../Translation";
import { ArrowDownIcon, ArrowsPointingOutIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { webhook } from "../../../../../Resources/Images";
import { TextInput } from "../../../../Inputs/TextInput";
import { TextArea } from "../../../../Inputs/TextArea";
import { Button } from "../../../../Buttons";
import Tabs, { useTabs } from "../../../../Tabs";
import { useAPI } from "../../../../../API";
import Select from "../../../../Inputs/Select";
import { bgColor, hoverBgColor } from "../../../../../Resources/CSS/styling";
import { escapeForBackend } from "../../../../../Utils";

const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate, userApp, appWebhookStep }) => {

    const { translate } = useTranslation();
    const api = useAPI();

    const [external_id, setExternalId] = useState(tmpStep.data.external_id);
    const [message, setMessage] = useState(tmpStep.data.message);
    const [title, setTitle] = useState(tmpStep.data.title);
    const [launchUrl, setLaunchUrl] = useState(tmpStep.data.launch_url);

    const onDataChange = (external_id, message, title, launch_url) => {

        const body = {
            app_id: userApp.data.app_id,
            target_channel: "push",
            url: launch_url ? launch_url : null,
            contents: {
                en: escapeForBackend(message)
            },
            headings: {
                en: escapeForBackend(title)
            },
            include_aliases: {
                external_id: [external_id]
            }
        }

        const APICallStepData = {
            method: 'POST',
            url: 'https://api.onesignal.com/notifications?c=push',
            body: body,
            headers: {
                'Authorization': 'Key ' + userApp.data.api_key
            },
            external_id: external_id,
            message: message,
            title: title,
            launch_url: launch_url
        }

        setTmpStep({ ...tmpStep, app_webhook_unique_identifier: appWebhookStep.webhook_unique_identifier, user_app_id: userApp.id, data: APICallStepData });
    }


    return (
        <div className="flex flex-col mt-4">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.external_id')}</p>
                <TextInput value={external_id} onChange={(e) => {setExternalId(e.target.value); onDataChange(e.target.value, message, title, launchUrl)}} />
            </div>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.title')}</p>
                <TextInput value={title} onChange={(e) => {setTitle(e.target.value); onDataChange(external_id, message, e.target.value, launchUrl)}} />
            </div>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.message')}</p>
                <TextArea value={message} onChange={(e) => {setMessage(e.target.value); onDataChange(external_id, e.target.value, title, launchUrl)}} />
            </div>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.launch_url')}</p>
                <TextInput value={launchUrl} onChange={(e) => {setLaunchUrl(e.target.value); onDataChange(external_id, message, title, e.target.value)}} />
            </div>
            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function OneSignalPushPanel({ setStepToClone, panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep, app, appWebhookStep, userApp }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);

    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {

            const step = {
                ...tmpStep,
                data: {
                    ...tmpStep.data,
                    headers: tmpStep.data.headers,
                    url: tmpStep.data.url,
                    method: tmpStep.data.method,
                    body: tmpStep.data.body
                }
            }

            if (isCreate) {
                try {
                    if (!step.description || step.description.trim() === '') {
                        step.description = "Send push notification from OneSignal";
                    }

                    const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, step);
                    if (newStepResponse?.data?.automatisation_step) {
                        await onCreateNewStep(newStepResponse.data.automatisation_step);
                    }
                } catch (e) {}
                
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, step);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }

    const selectStepToClone = (step) => {
        setStepToClone(step);
        panelHook.setOpen(false);
    }


    return (
        <div className="flex flex-col flex-1 h-min rounded-md p-4 bg-white overflow-hidden">
            <div className="flex flex-row items-center">
                <img src={app?.icon} alt="webhook" className="w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 text-black" />
                <h2 className="text-xl font-bold">{app?.name}</h2>
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                {!isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => selectStepToClone(tmpStep)}>{translate('clone')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} userApp={userApp} appWebhookStep={appWebhookStep} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}